/**
 * @file Helpers for adding script tags to the DOM.
 */

import { wvusLogger } from "../observability/logging";

/**
 * Adds a script element to the document head with the specified source URL and attributes.
 * Returns a Promise that resolves when the script is successfully loaded or rejects with an error if the script fails to load.
 *
 * @param {string} src - The source URL of the script to be added.
 * @param {Record<string, string>} [attributes={}] - An optional object containing additional attributes to set on the script element.
 * @returns {Promise<void | Error>} A Promise that resolves when the script is loaded or rejects with an error if loading fails.
 */
export function addScriptToHead(
  src: string,
  attributes: Record<string, string | boolean> = {}
): Promise<void | Error> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.setAttribute("src", src);

    // Add any additional attributes to the script tag
    if (attributes) {
      for (const key in attributes) {
        script.setAttribute(key, attributes[key].toString());
      }
    }

    // Resolve the Promise when the script is loaded
    script.addEventListener("load", () => {
      resolve();
    });

    // Reject the Promise if there's an error loading the script
    script.addEventListener("error", (event) => {
      const err = new Error(`Failed to load script: ${(event.target as HTMLScriptElement)?.src}`);
      wvusLogger.log(err, wvusLogger.getLogLevelNames().ERROR);
      reject(err);
    });

    document.head.appendChild(script);
  });
}

/**
 * Adds multiple scripts to the document head.
 *
 * @param listSrc - An array of tuples where each tuple contains a script source URL and an object of attributes to be added to the script element.
 * @returns A promise that resolves when all scripts have been added, with each promise indicating the success or failure of adding each script.
 *
 * @example
 * ```typescript
 * const scripts = [
 *   ["https://example.com/script1.js", { type: "module" }],
 *   ["https://example.com/script2.js", { async: true, defer: true }],
 *   ["https://example.com/script3.js"]
 * ];
 */
export function addScriptsToHead(listSrc: [string, Record<string, string>][]) {
  return Promise.allSettled(listSrc.map((args) => addScriptToHead(...args)));
}
